// CP Quality Pools proposal PDF (layout from docs/quote-template-example.pdf).
// quote_format 'proposal' (inground calculator bids) renders every section;
// 'standard' (above-ground / liner) uses the same styling with only the
// sections that apply: no specs, stages, coping, payment schedule, or lien notice.
import { jsPDF } from 'jspdf'
import { CP_QUALITY_LOGO } from '../assets/cpQualityLogo'
import { fmtDatePDF } from './pdfHelpers'
import {
  money, issuedDateFor, paymentSchedule, optionTotals, sortOptions, parseTerms,
  waterMitigationText, BUILD_STAGES, BUILD_STAGES_NOTE, LIEN_NOTICE,
} from './proposal'

var PW = 215.9, PH = 279.4          // US Letter, mm
var M = 9, CW = PW - 2 * M
var TOP = 24                        // content top on pages 2+ (below running header)
var BOTTOM = PH - 9

var DARK = [27, 30, 35]
var GOLD = [200, 160, 85]
var TEXT = [34, 34, 34]
var MUTED = [125, 125, 125]
var BOX = [245, 243, 238]
var ZEBRA = [250, 249, 246]
var RULE = [222, 219, 212]
var BASE_ROW = [248, 241, 226]

var ADDRESS = '11260 Dunham Rd, Hartland, MI 48353'
var CONTACT = '(810) 923-6229  •  carspie99@gmail.com'
var WEB = 'pietilagroup.com'

// Helvetica only covers WinAnsi — map the characters our content uses.
function clean(s) {
  return String(s == null ? '' : s)
    .replace(/′/g, "'").replace(/″/g, '"')
    .replace(/−/g, '-').replace(/☐/g, '')
}

function fmtQty(q) {
  var n = parseFloat(q)
  if (isNaN(n)) return ''
  return String(Math.round(n * 100) / 100)
}

export function generateProposalPDF(bid, lines, options) {
  var doc = new jsPDF({ unit: 'mm', format: 'letter', compress: true })
  var isProposal = bid.quote_format === 'proposal'
  var y = 0

  var fill = function(c) { doc.setFillColor(c[0], c[1], c[2]) }
  var stroke = function(c) { doc.setDrawColor(c[0], c[1], c[2]) }
  var color = function(c) { doc.setTextColor(c[0], c[1], c[2]) }
  var font = function(style, size) { doc.setFont('helvetica', style); doc.setFontSize(size) }
  var ptToMm = function(pt) { return pt * 0.3528 }

  var newPage = function() { doc.addPage('letter'); y = TOP }
  var need = function(h) { if (y + h > BOTTOM) newPage() }
  // Major sections start a new page like the template, unless the current
  // page is a mostly empty continuation.
  var freshPage = function() { if (y > PH * 0.45) newPage() }

  // jsPDF's getTextWidth applies kerning but text() draws without it, so long
  // strings measure short. Measure unkerned to match what is actually drawn.
  var tw = function(text) {
    return doc.getStringUnitWidth(text, { doKerning: false }) * doc.internal.getFontSize() / doc.internal.scaleFactor
  }
  var textAt = function(text, x, yy, align) {
    text = clean(text)
    var w = tw(text)
    doc.text(text, align === 'right' ? x - w : align === 'center' ? x - w / 2 : x, yy)
  }

  // Letter-spaced text; align is 'left' | 'right' | 'center'.
  var spaced = function(text, x, yy, cs, align) {
    text = clean(text)
    var w = tw(text) + cs * Math.max(text.length - 1, 0)
    var x0 = align === 'right' ? x - w : align === 'center' ? x - w / 2 : x
    doc.text(text, x0, yy, { charSpace: cs })
    return w
  }

  // Wrap text with **bold** spans into lines of runs [{text, bold, w}].
  var layoutRich = function(text, width, size, baseBold) {
    doc.setFontSize(size)
    var tokens = []
    var bold = !!baseBold
    clean(text).split('**').forEach(function(part, i) {
      if (i > 0) bold = !bold
      part.split(/( +|\n)/).forEach(function(t) { if (t) tokens.push({ text: t, bold: bold }) })
    })
    var out = [[]]
    var w = 0
    var last = function() { return out[out.length - 1] }
    tokens.forEach(function(t) {
      if (t.text === '\n') { out.push([]); w = 0; return }
      doc.setFont('helvetica', t.bold ? 'bold' : 'normal')
      if (/^ +$/.test(t.text)) {
        if (last().length) { var sw = tw(' '); last().push({ text: ' ', bold: t.bold, w: sw }); w += sw }
        return
      }
      var ww = tw(t.text)
      if (w + ww > width && last().length) {
        if (last()[last().length - 1].text === ' ') last().pop()
        out.push([]); w = 0
      }
      last().push({ text: t.text, bold: t.bold, w: ww }); w += ww
    })
    return out.filter(function(ln, i) { return ln.length || i < out.length - 1 })
  }

  var lineWidth = function(ln) { return ln.reduce(function(s, r) { return s + r.w }, 0) }

  // Draw laid-out lines. Single-weight lines go out as one string. Lines that
  // mix bold and regular place every word at its computed x: viewers render
  // the base-14 fonts slightly wider or narrower than jsPDF's metrics, and on
  // a long run that drift is enough to swallow the space before a bold word.
  var drawRich = function(rich, x, yy, size, lh, c, align, boxW) {
    doc.setFontSize(size); color(c)
    rich.forEach(function(ln, i) {
      var lw = lineWidth(ln)
      var cx = align === 'right' ? x + boxW - lw : align === 'center' ? x + (boxW - lw) / 2 : x
      var mixed = ln.some(function(r) { return r.bold !== ln[0].bold })
      if (mixed) {
        ln.forEach(function(r) {
          if (r.text !== ' ') { doc.setFont('helvetica', r.bold ? 'bold' : 'normal'); doc.text(r.text, cx, yy + i * lh) }
          cx += r.w
        })
        return
      }
      var buf = '', bufBold = null, bufW = 0
      var flush = function() {
        if (!buf) return
        doc.setFont('helvetica', bufBold ? 'bold' : 'normal')
        doc.text(buf, cx, yy + i * lh)
        cx += bufW; buf = ''; bufW = 0
      }
      ln.forEach(function(r) {
        if (bufBold !== null && r.bold !== bufBold) flush()
        bufBold = r.bold; buf += r.text; bufW += r.w
      })
      flush()
    })
  }

  var paragraph = function(text, o) {
    o = o || {}
    var size = o.size || 8.8, lh = o.lh || ptToMm(size) * 1.45
    var x = o.x || M, w = o.w || CW
    var rich = layoutRich(text, w, size, o.bold)
    rich.forEach(function(ln) {
      need(lh)
      drawRich([ln], x, y + ptToMm(size) * 0.8, size, lh, o.color || TEXT, o.align, w)
      y += lh
    })
    y += o.after != null ? o.after : 2
  }

  // keep = height of the content that must follow the title on the same page.
  var section = function(title, keep) {
    need(8 + (keep || 12))
    y += 2.2
    font('bold', 8.5); color(DARK)
    spaced(title.toUpperCase(), M, y, 0.9, 'left')
    y += 2.3
    stroke(GOLD); doc.setLineWidth(0.35); doc.line(M, y, M + CW, y)
    y += 3.4
  }

  // Two-column bullet list, filled column by column like the template.
  var bullets2 = function(items) {
    if (!items || !items.length) return
    var size = 8.3, lh = ptToMm(size) * 1.28, gap = 8
    var colW = (CW - gap) / 2, textW = colW - 4.5
    var half = Math.ceil(items.length / 2)
    var cols = [items.slice(0, half), items.slice(half)].map(function(list) {
      return list.map(function(it) { return layoutRich(it, textW, size) })
    })
    var colH = function(col) { return col.reduce(function(s, r) { return s + r.length * lh + 0.6 }, 0) }
    var h = Math.max(colH(cols[0]), colH(cols[1]))
    if (h > BOTTOM - TOP) {
      // Too long for two columns on one page: fall back to a single column.
      items.forEach(function(it) {
        var r = layoutRich(it, CW - 4.5, size)
        need(r.length * lh + 0.6)
        font('normal', size); color(TEXT); doc.text('•', M + 1, y + ptToMm(size) * 0.8)
        drawRich(r, M + 4.5, y + ptToMm(size) * 0.8, size, lh, TEXT)
        y += r.length * lh + 0.6
      })
      y += 2
      return
    }
    need(h)
    cols.forEach(function(col, ci) {
      var x = M + ci * (colW + gap), cy = y
      col.forEach(function(r) {
        font('normal', size); color(TEXT); doc.text('•', x + 1, cy + ptToMm(size) * 0.8)
        drawRich(r, x + 4.5, cy + ptToMm(size) * 0.8, size, lh, TEXT)
        cy += r.length * lh + 0.6
      })
    })
    y += h + 2
  }

  // Table with a dark header row, zebra rows, wrapped cells, and page breaks
  // that repeat the header. cell: string | { text, bold, detail, color, badge }
  // o.measureOnly returns the table's height without drawing it.
  var table = function(cols, rows, o) {
    o = o || {}
    var size = o.size || 8, lh = ptToMm(size) * 1.3, padY = 1.55, padX = 2.5, headH = 6.4
    var header = function() {
      fill(DARK); doc.rect(M, y, CW, headH, 'F')
      font('bold', 6.3); doc.setTextColor(255, 255, 255)
      var x = M
      cols.forEach(function(c) {
        var ax = c.align === 'right' ? x + c.w - padX : c.align === 'center' ? x + c.w / 2 : x + padX
        spaced(c.h || '', ax, y + 4.4, 0.55, c.align || 'left')
        x += c.w
      })
      y += headH
    }
    var measure = function(r) {
      return cols.map(function(c, ci) {
        var cell = r.cells[ci]
        if (cell == null || typeof cell !== 'object') cell = { text: cell == null ? '' : String(cell) }
        var w = c.w - 2 * padX
        if (cell.badge) { font('bold', 5.6); w -= tw(cell.badge) + 8 }
        var rich = layoutRich(cell.text, w, size, cell.bold || c.bold)
        var drich = cell.detail ? layoutRich(cell.detail, c.w - 2 * padX, size - 1) : []
        var h = rich.length * lh + (drich.length ? 0.8 + drich.length * (lh - 0.3) : 0)
        return { cell: cell, rich: rich, drich: drich, h: h }
      })
    }
    var rowHeight = function(ms) { return Math.max.apply(null, ms.map(function(m) { return m.h })) + 2 * padY }
    if (o.measureOnly) return rows.reduce(function(s, r) { return s + rowHeight(measure(r)) }, headH)
    need(headH + 12); header()
    rows.forEach(function(r, i) {
      var ms = measure(r)
      var h = rowHeight(ms)
      if (y + h > BOTTOM) { newPage(); header() }
      var bg = r.fill || (i % 2 === 0 ? ZEBRA : null)
      if (bg) { fill(bg); doc.rect(M, y, CW, h, 'F') }
      var x = M
      var base = y + padY + ptToMm(size) * 0.8
      ms.forEach(function(m, ci) {
        var c = cols[ci]
        drawRich(m.rich, x + padX, base, size, lh, m.cell.color || TEXT, c.align, c.w - 2 * padX)
        if (m.cell.badge) {
          var bx = x + padX + lineWidth(m.rich[0] || []) + 2.5
          font('bold', 5.6)
          var bw = tw(m.cell.badge) + 4 + 0.35 * (m.cell.badge.length - 1)
          fill(GOLD); doc.roundedRect(bx, base - 2.9, bw, 3.9, 0.6, 0.6, 'F')
          color(DARK); spaced(m.cell.badge, bx + 2, base - 0.1, 0.35, 'left')
        }
        if (m.drich.length) drawRich(m.drich, x + padX, base + m.rich.length * lh + 0.8, size - 1, lh - 0.3, MUTED, 'left', c.w - 2 * padX)
        x += c.w
      })
      stroke(RULE); doc.setLineWidth(0.15); doc.line(M, y + h, M + CW, y + h)
      y += h
    })
  }

  // Full-width summary row: label right-aligned before the last column, value in it.
  var summaryRow = function(label, value, o) {
    o = o || {}
    var h = o.h || 8, lastW = o.lastW || 34
    need(h)
    fill(o.fill || BOX); doc.rect(M, y, CW, h, 'F')
    font('bold', o.size || 8.4); color(o.color || TEXT)
    textAt(label, M + CW - lastW - 3, y + h / 2 + 1.2, 'right')
    textAt(value, M + CW - 2.5, y + h / 2 + 1.2, 'right')
    if (o.gold) { stroke(GOLD); doc.setLineWidth(0.6); doc.line(M, y + h, M + CW, y + h) }
    y += h
  }

  // Bordered note box with a spaced title.
  var noteBox = function(title, text, size) {
    size = size || 8.2
    var lh = ptToMm(size) * 1.4
    var rich = layoutRich(text, CW - 10, size)
    var h = 5 + 5 + rich.length * lh + 3
    need(h)
    fill(BOX); stroke([200, 196, 186]); doc.setLineWidth(0.25); doc.rect(M, y, CW, h, 'FD')
    font('bold', 7.6); color(DARK); spaced(title.toUpperCase(), M + 5, y + 6, 0.7, 'left')
    drawRich(rich, M + 5, y + 10.5 + ptToMm(size) * 0.5, size, lh, TEXT)
    y += h + 3
  }

  // ===== Data =====
  var number = bid.estimate_number || 'DRAFT'
  var client = bid.client_name || ''
  var clientLines = (lines || []).filter(function(l) { return l.category !== 'notes' && parseFloat(l.total_cost) > 0 })
  var notesLine = (lines || []).find(function(l) { return l.category === 'notes' && l.description })
  var baseLines = clientLines.filter(function(l) { return l.section === 'base' })
  var otherLines = clientLines.filter(function(l) { return l.section !== 'base' })
  var opts = isProposal ? sortOptions(options) : []
  var baseOption = opts.find(function(o) { return o.is_base })
  var water = parseFloat(bid.water_mitigation_amount) > 0 ? parseFloat(bid.water_mitigation_amount) : null
  var total = parseFloat(bid.total) || 0

  // ===== Page 1 header =====
  fill(DARK); doc.rect(M, 8, CW, 25, 'F')
  try { doc.addImage(CP_QUALITY_LOGO, 'PNG', M + 5, 10, 21, 21, 'cpq', 'FAST') } catch (e) {} // eslint-disable-line no-unused-vars
  font('bold', 16); doc.setTextColor(255, 255, 255); doc.text('CP Quality Pools', M + 30, 20)
  font('bold', 6.2); color(GOLD); spaced('PIETILA GROUP  •  EST. 2017', M + 30, 25.5, 1.1, 'left')
  font('bold', 10.5); color(GOLD); spaced('PROPOSAL', M + CW - 5, 15.8, 2, 'right')
  font('normal', 7.2); doc.setTextColor(175, 175, 175)
  textAt(ADDRESS, M + CW - 5, 21.5, 'right')
  textAt(CONTACT, M + CW - 5, 25.1, 'right')
  textAt(WEB, M + CW - 5, 28.7, 'right')
  y = 36

  // ===== Prepared For / Proposal Details =====
  var boxW = (CW - 6) / 2
  var addr = String(bid.site_address || '')
  var comma = addr.indexOf(',')
  var addrLines = comma > 0 ? [addr.slice(0, comma).trim(), addr.slice(comma + 1).trim()] : (addr ? [addr] : [])
  var details = [
    ['Proposal No.', number, true],
    ['Date Issued', fmtDatePDF(issuedDateFor(bid))],
    ['Valid Through', bid.valid_until ? fmtDatePDF(bid.valid_until) : ''],
    ['Project', bid.job_name || ''],
  ].filter(function(d) { return d[1] })
  font('normal', 8.4)
  var detailRows = details.map(function(d) {
    return { label: d[0], bold: d[2], lines: doc.splitTextToSize(clean(d[1]), boxW - 38) }
  })
  var detailH = detailRows.reduce(function(s, d) { return s + d.lines.length * 3.9 }, 0)
  var boxH = Math.max(11.5 + 4.8 + addrLines.length * 4, 11 + detailH) + 1.5
  ;[0, 1].forEach(function(i) {
    var bx = M + i * (boxW + 6)
    fill(BOX); doc.rect(bx, y, boxW, boxH, 'F')
    fill(GOLD); doc.rect(bx, y, 0.9, boxH, 'F')
  })
  font('bold', 6.3); color(MUTED)
  spaced('PREPARED FOR', M + 6, y + 5.8, 0.9, 'left')
  spaced('PROPOSAL DETAILS', M + boxW + 12, y + 5.8, 0.9, 'left')
  font('bold', 10.5); color(TEXT); doc.text(clean(client) || '—', M + 6, y + 11.5)
  font('normal', 8.6)
  addrLines.forEach(function(a, i) { doc.text(clean(a), M + 6, y + 16.3 + i * 4) })
  var dy = y + 11.2
  detailRows.forEach(function(d) {
    font('normal', 8.4); color(TEXT); doc.text(d.label, M + boxW + 12, dy)
    font(d.bold ? 'bold' : 'normal', 8.4)
    d.lines.forEach(function(ln, li) { textAt(ln, M + 2 * boxW + 6 - 5, dy + li * 3.9, 'right') })
    dy += d.lines.length * 3.9
  })
  y += boxH + 3

  // ===== Project Overview =====
  if (bid.description) {
    section('Project Overview')
    paragraph(bid.description, { size: 8.6, lh: ptToMm(8.6) * 1.38 })
  }

  if (isProposal) {
    // ===== Pool Specifications =====
    var specs = (bid.pool_specs || []).filter(function(s) { return s && (s.label || s.value) })
    if (specs.length) {
      section('Pool Specifications')
      bullets2(specs.map(function(s) { return s.label ? '**' + s.label + ':** ' + (s.value || '') : s.value }))
      if (bid.pool_specs_note) paragraph('**Please note:** ' + bid.pool_specs_note, { size: 7.4, color: MUTED })
    }

    // ===== Scope of Work =====
    if ((bid.scope_items || []).length) {
      section('Scope of Work — Pool Construction')
      bullets2(bid.scope_items)
    }

    // ===== Equipment Package =====
    if ((bid.equipment_items || []).length) {
      section('Equipment Package — Included')
      bullets2(bid.equipment_items)
    }

    // ===== How the Build Runs (kept on one page) =====
    var stageCols = [{ h: 'STAGE', w: 16, align: 'center' }, { h: 'PHASE', w: 50, bold: true }, { h: 'WHAT HAPPENS', w: CW - 66 }]
    var stageRows = BUILD_STAGES.map(function(s, i) { return { cells: [String(i + 1), s.phase, s.what] } })
    section('How the Build Runs', table(stageCols, stageRows, { measureOnly: true }) + 4)
    table(stageCols, stageRows)
    y += 1
    paragraph(BUILD_STAGES_NOTE, { size: 7.2, lh: ptToMm(7.2) * 1.3, color: MUTED })
    freshPage()
  }

  // ===== Investment =====
  section('Investment', 24)
  var invCols = [
    { h: 'DESCRIPTION', w: 96 }, { h: 'QTY', w: 16, align: 'center' }, { h: 'UNIT', w: 18, align: 'center' },
    { h: 'RATE', w: 28, align: 'right' }, { h: 'AMOUNT', w: CW - 158, align: 'right' },
  ]
  var lineRow = function(l, isBase) {
    var rate = parseFloat(l.unit_cost)
    return { cells: [
      { text: l.description || '', bold: isBase, detail: l.detail || '' },
      fmtQty(l.quantity), l.unit || '',
      isBase || !(rate > 0) ? '—' : money(rate),
      money(l.total_cost),
    ] }
  }
  var invRows = baseLines.map(function(l) { return lineRow(l, true) }).concat(otherLines.map(function(l) { return lineRow(l, false) }))
  if (invRows.length) table(invCols, invRows)
  if (baseLines.length && otherLines.length) {
    var siteSum = otherLines.reduce(function(s, l) { return s + (parseFloat(l.total_cost) || 0) }, 0)
    summaryRow('Subtotal — Site Work & Utilities', money(siteSum), { lastW: CW - 158 })
  }
  need(11)
  fill(DARK); doc.rect(M, y, CW, 10, 'F')
  stroke(GOLD); doc.setLineWidth(0.7); doc.line(M, y + 10, M + CW, y + 10)
  font('bold', 10.5); doc.setTextColor(255, 255, 255)
  textAt(opts.length ? 'PROJECT TOTAL — as configured below' : 'PROJECT TOTAL', M + CW - (CW - 158) - 3, y + 6.6, 'right')
  textAt(money(total), M + CW - 2.5, y + 6.6, 'right')
  y += 13
  if (notesLine) paragraph(notesLine.description, { size: 7.6, color: MUTED })

  // ===== Coping Options =====
  if (opts.length) {
    section('Coping Options', 30)
    paragraph('Coping is the finished cap where the pool wall meets the deck. Select one — the project total adjusts as shown.', { size: 7.4, color: MUTED, after: 1.5 })
    table(
      [{ h: 'OPTION', w: 104 }, { h: '', w: 28 }, { h: 'ADJUSTMENT', w: 30, align: 'right' }, { h: 'PROJECT TOTAL', w: CW - 162, align: 'right' }],
      optionTotals(bid, opts).map(function(t) {
        var o = t.option
        var kind = o.is_base ? 'Included' : t.adjustment < 0 ? 'Credit' : 'Upgrade'
        var adj = t.adjustment === 0 ? '—' : (t.adjustment > 0 ? '+' : '-') + money(Math.abs(t.adjustment))
        return {
          fill: o.is_base ? BASE_ROW : null,
          cells: [{ text: o.label, bold: true, badge: o.is_base ? 'BASE CONFIGURATION' : null }, kind, adj, { text: money(t.total), bold: true }],
        }
      })
    )
    y += 2
  }

  // ===== Water Mitigation =====
  if (water) {
    section('Water Mitigation', 26)
    noteBox(money(water).replace(/\.00$/, '') + ' Water Mitigation Clause', waterMitigationText(water))
  }

  // ===== Not Included =====
  if ((bid.not_included || []).length) {
    section('Not Included')
    bullets2(bid.not_included)
  }

  // ===== Payment Schedule =====
  if (isProposal) {
    freshPage()
    section('Payment Schedule', 30)
    var sched = paymentSchedule(bid, total)
    table(
      [{ h: '#', w: 12, align: 'center' }, { h: 'MILESTONE', w: 44, bold: true }, { h: 'DUE', w: CW - 90 }, { h: 'AMOUNT', w: 34, align: 'right' }],
      sched.rows.map(function(r, i) { return { cells: [String(i + 1), r.milestone, r.due, money(r.amount)] } })
    )
    summaryRow('Contract Price' + (baseOption ? ' (' + baseOption.label.toLowerCase() + ')' : ''), money(sched.total), { lastW: 34, gold: true })
    y += 2
    var note = 'The retainer is ' + sched.retainerPct + '% of the contract price.'
    var upgrades = optionTotals(bid, opts).filter(function(t) { return !t.option.is_base && t.adjustment !== 0 })
    if (upgrades.length) {
      note += ' A coping upgrade changes the contract price, and the water-fill payment and retainer adjust with it: '
        + upgrades.map(function(t) { return t.option.label.toLowerCase() + ' — water fill ' + money(t.waterFill) + ', retainer ' + money(t.retainer) }).join('; ') + '.'
    }
    if (water) note += ' If the water mitigation charge is triggered, it is added to the water-fill payment.'
    paragraph(note, { size: 7.2, color: MUTED })
  }

  // ===== Terms & Conditions =====
  var terms = parseTerms(bid.disclaimers)
  if (terms.items.length || terms.preamble) {
    section('Terms & Conditions')
    if (terms.preamble) paragraph(terms.preamble, { size: 7.8 })
    terms.items.forEach(function(t) {
      var size = 7.8, lh = ptToMm(size) * 1.42
      var rich = layoutRich('**' + t.title + '.** ' + t.body, CW - 5, size)
      need(Math.min(rich.length, 3) * lh)
      font('normal', size); color(TEXT); doc.text(t.n + '.', M, y + ptToMm(size) * 0.8)
      rich.forEach(function(ln) {
        need(lh)
        drawRich([ln], M + 5, y + ptToMm(size) * 0.8, size, lh, TEXT)
        y += lh
      })
      y += 1.2
    })
    y += 1.5
  }

  // ===== Lien notice =====
  if (isProposal) noteBox('Michigan Construction Lien Act Notice', LIEN_NOTICE, 7.2)

  // ===== Acceptance =====
  // Keep the whole block (text, coping choices, signature lines, footer) together.
  section('Acceptance', 56 + (opts.length ? 12 : 0))
  var accepted = ['the scope', 'pricing']
  if (opts.length) accepted.push('coping selection')
  if (water) accepted.push('water mitigation clause')
  if (isProposal) accepted.push('payment schedule')
  accepted.push('terms')
  var acceptList = accepted.slice(0, -1).join(', ') + ', and ' + accepted[accepted.length - 1]
  paragraph('By signing below, the owner accepts this proposal — including ' + acceptList + ' — and authorizes CP Quality Pools to proceed.', { size: 8.2, after: 2.5 })

  if (opts.length) {
    var size = 8.2
    need(10)
    font('bold', size); color(TEXT)
    var cx = M, cy = y + ptToMm(size) * 0.8
    doc.text('Coping selection:', cx, cy)
    cx += tw('Coping selection:') + 4
    optionTotals(bid, opts).forEach(function(t) {
      var label = clean(t.option.label) + (t.option.is_base ? ' (included)' : ' (' + (t.adjustment >= 0 ? '+' : '-') + money(Math.abs(t.adjustment)) + ')')
      font('normal', size)
      var w = 3 + 1.8 + tw(label)
      if (cx + w > M + CW) { cx = M; cy += 5; y += 5 }
      stroke(TEXT); doc.setLineWidth(0.25); doc.rect(cx, cy - 2.6, 2.8, 2.8)
      doc.text(label, cx + 4.6, cy)
      cx += w + 6
    })
    y += 8
  }

  need(34)
  var sigW = (CW - 10) / 2
  var sigLine = function(x, yy, label) {
    stroke([110, 110, 110]); doc.setLineWidth(0.25); doc.line(x, yy, x + sigW, yy)
    font('normal', 6.3); color(MUTED); spaced(label, x, yy + 4, 0.8, 'left')
  }
  sigLine(M, y + 12, 'OWNER SIGNATURE  •  DATE')
  sigLine(M + sigW + 10, y + 12, 'OWNER SIGNATURE  •  DATE')
  sigLine(M, y + 26, 'CP QUALITY POOLS  •  DATE')
  y += 34

  // ===== Footer (end of document) =====
  need(10)
  stroke(GOLD); doc.setLineWidth(0.35); doc.line(M, y, M + CW, y)
  font('normal', 7.2); color(MUTED)
  textAt('CP Quality Pools  •  ' + ADDRESS + '  •  (810) 923-6229  •  ' + WEB, PW / 2, y + 5, 'center')

  // ===== Running header on pages 2+ =====
  var pages = doc.internal.getNumberOfPages()
  for (var p = 2; p <= pages; p++) {
    doc.setPage(p)
    font('bold', 12); color(DARK); doc.text('CP Quality Pools', M, 15)
    font('normal', 7.4); color(MUTED)
    textAt('Proposal ' + number + (client ? '  •  ' + client : '') + '  •  Page ' + p + ' of ' + pages, M + CW, 15, 'right')
    stroke(GOLD); doc.setLineWidth(0.5); doc.line(M, 18.5, M + CW, 18.5)
  }

  return doc
}
